import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { usePostHog } from "posthog-js/react";

import { Card, CardContent, Skeleton } from "@kuzco/web/ui";

import type { LeaderboardRange } from "../../server/routers/leaderboard.helpers";
import { authClient } from "../../auth-client";
import { CurrentUserCard } from "../../components/leaderboard/CurrentUserCard";
import { LeaderboardPagination } from "../../components/leaderboard/LeaderboardPagination";
import { LeaderboardRangePicker } from "../../components/leaderboard/LeaderboardRangePicker";
import { LeaderboardTable } from "../../components/leaderboard/LeaderboardTable";
import { nubankTrpc } from "../../trpc";

export const Route = createFileRoute("/_authed/leaderboard")({
  component: LeaderboardPage,
  head: () => ({
    meta: [{ title: "Leaderboard | NBDC" }],
  }),
});

function LeaderboardPage() {
  const { data: session } = authClient.useSession();
  const currentUserId = session?.user.id ?? null;
  const posthog = usePostHog();

  const [range, setRange] = useState<LeaderboardRange>("today");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const query = nubankTrpc.leaderboard.getData.useQuery({
    range,
    page,
    pageSize,
  });

  const handleRangeChange = useCallback(
    (next: LeaderboardRange) => {
      setRange(next);
      setPage(1);
      posthog.capture("leaderboard_range_changed", { range: next });
    },
    [posthog],
  );

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const leaderboard = query.data?.leaderboard ?? [];
  const currentUserPosition = query.data?.currentUserPosition ?? null;
  const totalUsers = query.data?.totalUsers ?? 0;
  const totalPages = query.data?.totalPages ?? 0;

  const currentUserOnPage = useMemo(
    () =>
      currentUserId !== null &&
      leaderboard.some((entry) => entry.userId === currentUserId),
    [leaderboard, currentUserId],
  );

  const showCurrentUserCard =
    !currentUserOnPage && !query.isPending && !query.isFetching;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-yellow-500" />
        <h1 className="text-3xl font-semibold tracking-tight">Leaderboard</h1>
      </div>

      {showCurrentUserCard && (
        <CurrentUserCard position={currentUserPosition} />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <LeaderboardRangePicker value={range} onChange={handleRangeChange} />
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isPending ? (
            <div className="flex flex-col gap-2 p-6">
              {Array.from({ length: pageSize }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <p className="p-6 text-sm text-detail-failure">
              {query.error.message}
            </p>
          ) : leaderboard.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No activity found for this period.
            </p>
          ) : (
            <LeaderboardTable
              entries={leaderboard}
              currentUserId={currentUserId}
            />
          )}
        </CardContent>
      </Card>

      <LeaderboardPagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalUsers={totalUsers}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
